import React, { useMemo, useState } from 'react'

import {
    Box,
    Button,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { Link } from 'react-router-dom'

import { EzosLoader } from '../../../components'
import { useApartmentProspects } from '../../../hooks/Outreach/useApartmentProspects'
import { useCampaign } from '../../../hooks/Outreach/useCampaign'
import { useUser } from '../../../hooks/useUser'
import {
    PROPERTY_DETAILS_TAB,
    propertyDetailsPath,
} from '../../../main/containers/settings/PropertyDetails/propertyDetailsTabUtils'
import { ProspectsSectionHeader } from './ProspectsSectionHeader'
import { SchoolProspectsTable } from './components/SchoolProspectsTable'

const useStyles = makeStyles((theme: Theme) => ({
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
}))

export const ProspectsHome: React.FC = () => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined
    const {
        summary,
        isLoadingSummary,
        browse,
        isLoadingBrowse,
        filters,
        setSchoolId,
        setSearch,
        setGraduationYears,
        setHasEmail,
        setHasPhone,
        setStatusSort,
        setPage,
        setPageSize,
        enrichProspectField,
        enrichingProspects,
        addProspectToCampaign,
    } = useApartmentProspects(apartmentId)

    const { campaigns } = useCampaign()
    const [searchInput, setSearchInput] = useState('')

    const servedSchools = useMemo(() => summary?.schools ?? [], [
        summary?.schools,
    ])

    const tabIndex = servedSchools.findIndex(
        (s) => s.school_id === filters.schoolId,
    )

    const loading = isLoadingSummary || isLoadingBrowse

    return (
        <Box p={3}>
            <ProspectsSectionHeader />

            {isLoadingSummary && !summary && <EzosLoader />}

            {servedSchools.length > 0 && (
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_e, idx) => {
                        const school = servedSchools[idx]
                        if (school) setSchoolId(school.school_id)
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {servedSchools.map((school) => (
                        <Tab
                            key={school.school_id}
                            label={`${school.effective_display_name} (${school.prospect_count})`}
                        />
                    ))}
                </Tabs>
            )}

            {servedSchools.length === 0 && !loading && (
                <Box className={classes.emptyState}>
                    <Typography variant="h6">
                        No served schools configured
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Configure which schools your property serves for
                        prospect outreach.
                    </Typography>
                    <Button
                        component={Link}
                        to={propertyDetailsPath(
                            PROPERTY_DETAILS_TAB.schoolsServed,
                        )}
                        color="primary"
                        variant="contained"
                    >
                        Configure schools
                    </Button>
                </Box>
            )}

            {loading && servedSchools.length > 0 && <EzosLoader />}

            {servedSchools.length > 0 && !isLoadingSummary && (
                <SchoolProspectsTable
                    prospects={browse?.results ?? []}
                    totalCount={browse?.count ?? 0}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    searchInput={searchInput}
                    onSearchInputChange={setSearchInput}
                    onSearchSubmit={() => setSearch(searchInput)}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    isLoading={isLoadingBrowse}
                    graduationYears={filters.graduationYears}
                    onGraduationYearsChange={setGraduationYears}
                    hasEmail={filters.hasEmail}
                    onHasEmailChange={setHasEmail}
                    hasPhone={filters.hasPhone}
                    onHasPhoneChange={setHasPhone}
                    statusSort={filters.statusSort}
                    onStatusSortChange={setStatusSort}
                    showStatusColumn
                    enrichingProspects={enrichingProspects}
                    enrichProspectField={enrichProspectField}
                    campaigns={campaigns ?? []}
                    addProspectToCampaign={addProspectToCampaign}
                />
            )}
        </Box>
    )
}
